import {
  Activity,
  Building2,
  Key,
  KeyRound,
  LayoutGrid,
  PanelLeft,
  Route as RouteIcon,
  Scale as ScaleIcon,
  Tags,
  Users,
} from "lucide-react"
import { NavLink, Outlet } from "react-router"

import { clearSesion } from "@/api/auth"
import { useYo } from "@/api/queries"
import { Logo } from "@/app/logo"
import { ModeToggle } from "@/app/mode-toggle"
import { useNavPlegada } from "@/app/nav-plegada"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/**
 * `permiso: null` = lo ve cualquiera que haya entrado.
 *
 * El resto se filtra con los permisos que devuelve `/admin/auth/me`, **no con el rol**.
 * Comparar contra el rol aquí sería reimplementar la tabla de roles en el navegador, y
 * dos tablas acaban diciendo cosas distintas.
 */
const NAV = [
  { to: "/", etiqueta: "Consumo", Icono: Activity, end: true, permiso: "ver_consumo" },
  { to: "/apps", etiqueta: "Aplicaciones", Icono: LayoutGrid, end: false, permiso: "ver_consumo" },
  { to: "/aliases", etiqueta: "Alias", Icono: Tags, end: false, permiso: "ver_consumo" },
  { to: "/prices", etiqueta: "Precios", Icono: Key, end: false, permiso: "ver_consumo" },
  { to: "/traces", etiqueta: "Trazas", Icono: RouteIcon, end: false, permiso: "ver_consumo" },
  // Junto a Precios y no escondido en Organización: quien mira si la facturación
  // cuadra es quien mira los precios, y separarlos haría que nadie entrara nunca.
  {
    to: "/reconciliations",
    etiqueta: "Cuadre",
    Icono: ScaleIcon,
    end: false,
    permiso: "ver_consumo",
  },
  {
    to: "/organization",
    etiqueta: "Organización",
    Icono: Building2,
    end: false,
    permiso: null,
  },
  {
    to: "/credentials",
    etiqueta: "Credenciales",
    Icono: KeyRound,
    end: false,
    permiso: "gestionar_credenciales",
  },
  {
    to: "/operators",
    etiqueta: "Personas",
    Icono: Users,
    end: false,
    permiso: "gestionar_operadores",
  },
]

const ROL: Record<string, string> = {
  owner: "propietario",
  admin: "administrador",
  viewer: "solo lectura",
}

export function Layout({ onSalir }: { onSalir: () => void }) {
  const yo = useYo()
  const [plegada, alternar] = useNavPlegada()
  const permisos = yo.data?.permisos ?? []

  // Mientras no se sepa qué puede hacer, no se enseña nada que pueda no poder: pintar
  // un enlace y quitarlo medio segundo después es peor que esperar medio segundo.
  const visibles = yo.isPending
    ? []
    : NAV.filter((n) => n.permiso === null || permisos.includes(n.permiso))

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-svh bg-background text-foreground">
        <aside
          className={cn(
            "hidden shrink-0 border-r bg-sidebar p-3 transition-[width] duration-200 md:block",
            plegada ? "w-16" : "w-60",
          )}
        >
          <div className={cn("mb-6 flex h-8 items-center", plegada ? "justify-center" : "px-2")}>
            {/* Plegada solo cabe la A con las orejas. No es un recorte del logo: es el
                icono de marca, pensado para leerse a ese tamaño. */}
            <Logo
              variante={plegada ? "icono" : "completo"}
              className={plegada ? "size-8" : "h-8 w-auto"}
            />
          </div>

          <nav className="space-y-1">
            {yo.isPending
              ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)
              : visibles.map(({ to, etiqueta, Icono, end }) => (
                  <Tooltip key={to}>
                    {/* El disparador es un `span` que envuelve al enlace, no el enlace
                        mismo. Con `asChild`, Radix clona al hijo pasándole `className`
                        como CADENA, y `NavLink` lo recibe como FUNCIÓN —la que decide
                        el estado activo—: una pisa a la otra y el enlace se queda sin
                        estilos, con el icono encima de la etiqueta en vez de al lado.
                        El nombre accesible del enlace lo da su `aria-label`, así que no
                        se pierde nada por no ser él el disparador. */}
                    <TooltipTrigger asChild>
                      <span className="block">
                        <NavLink
                          to={to}
                          end={end}
                          // El nombre accesible NO puede depender de si está plegada:
                          // sin esto, un lector de pantalla se queda sin saber a dónde
                          // lleva cada icono en cuanto se pliega el menú.
                          aria-label={etiqueta}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
                              plegada ? "justify-center px-0" : "px-2",
                              isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground",
                            )
                          }
                        >
                          <Icono className="size-4 shrink-0" />
                          {!plegada && etiqueta}
                        </NavLink>
                      </span>
                    </TooltipTrigger>
                    {/* El tooltip solo cuando hace falta: con la etiqueta a la vista,
                        repetirla al pasar el ratón es ruido. */}
                    {plegada && <TooltipContent side="right">{etiqueta}</TooltipContent>}
                  </Tooltip>
                ))}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center gap-3 border-b px-4">
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              aria-label={plegada ? "Desplegar el menú" : "Plegar el menú"}
              aria-expanded={!plegada}
              onClick={alternar}
            >
              <PanelLeft className="size-4" />
            </Button>

            <div className="flex-1" />

            {yo.data !== undefined && (
              <Badge variant="secondary" className="font-normal">
                {ROL[yo.data.role] ?? yo.data.role}
              </Badge>
            )}
            <ModeToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clearSesion()
                onSalir()
              }}
            >
              Salir
            </Button>
          </header>
          <main className="min-w-0 flex-1 p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
